interface RoomInfo {
  roomId: string;
  clients: number;
  maxClients: number;
  metadata?: { roomName?: string; botCount?: number; minimumPlayers?: number; roundDuration?: number; difficulty?: string; obstacleDensity?: number };
}

// ---------------------------------------------------------------------------
// Admin gate
// ---------------------------------------------------------------------------

let adminPassword = sessionStorage.getItem('rttv_admin_token') ?? '';

function adminHeaders(): HeadersInit {
  return { 'X-Admin-Password': adminPassword };
}

async function initAdminGate(): Promise<void> {
  const gate      = document.getElementById('admin-gate')!;
  const panel     = document.getElementById('manage-panel')!;
  const form      = document.getElementById('ag-form')!;
  const input     = document.getElementById('ag-password') as HTMLInputElement;
  const btn       = document.getElementById('ag-btn') as HTMLButtonElement;
  const errorEl   = document.getElementById('ag-error')!;

  // Already authenticated this session
  if (adminPassword) {
    gate.classList.add('hidden');
    panel.style.display = '';
    initManagement();
    return;
  }

  input.focus();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const password = input.value;
    if (!password) return;
    btn.disabled = true;
    errorEl.textContent = '';
    try {
      const resp = await fetch('/api/admin-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (resp.ok) {
        adminPassword = password;
        sessionStorage.setItem('rttv_admin_token', password);
        gate.classList.add('hidden');
        panel.style.display = '';
        initManagement();
      } else {
        errorEl.textContent = 'INVALID PASSWORD';
        input.select();
        btn.disabled = false;
      }
    } catch {
      errorEl.textContent = 'SERVER UNREACHABLE';
      btn.disabled = false;
    }
  });
}

// ---------------------------------------------------------------------------
// Management UI — only wired up after auth passes
// ---------------------------------------------------------------------------

function initManagement(): void {
  const voidList    = document.getElementById('void-list')!;
  const newNameInput = document.getElementById('new-void-name') as HTMLInputElement;
  const createBtn   = document.getElementById('create-btn')    as HTMLButtonElement;
  const refreshBtn  = document.getElementById('refresh-btn')!;
  const statusMsg   = document.getElementById('status-msg')!;

  function setStatus(msg: string, isError = false): void {
    statusMsg.textContent = msg;
    statusMsg.className   = isError ? 'status-error' : 'status-ok';
  }

  async function fetchRooms(): Promise<void> {
    voidList.innerHTML = '<div class="msg-loading">SCANNING\u2026</div>';
    try {
      const resp = await fetch('/api/rooms');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const rooms = await resp.json() as RoomInfo[];
      renderRooms(rooms);
    } catch (err) {
      voidList.innerHTML = `<div class="msg-error">FAILED TO REACH SERVER: ${escHtml(String(err))}</div>`;
    }
  }

  function renderRooms(rooms: RoomInfo[]): void {
    if (rooms.length === 0) {
      voidList.innerHTML = '<div class="msg-empty">NO ACTIVE VOIDS</div>';
      return;
    }
    voidList.innerHTML = '';
    for (const room of rooms) {
      const name = room.metadata?.roomName ?? `Void ${room.roomId.slice(0, 6).toUpperCase()}`;
      const row  = document.createElement('div');
      row.className = 'room-row';
      const meta = room.metadata ?? {};
      const dur = meta.roundDuration ? `${meta.roundDuration / 60} MIN` : '—';
      const density = meta.obstacleDensity === 0 ? 'NONE' : meta.obstacleDensity === 0.25 ? 'SPARSE' : meta.obstacleDensity === 0.5 ? 'MODERATE' : meta.obstacleDensity === 1 ? 'DENSE' : '—';
      const diff = meta.difficulty ?? '—';
      const minP = meta.minimumPlayers ?? 0;
      row.innerHTML = `
        <div class="room-info">
          <span class="room-name" title="${escHtml(name)}">${escHtml(name)}</span>
          <span class="room-settings">${dur} &middot; ${density} &middot; ${minP} BOTS &middot; ${escHtml(diff.toUpperCase())}</span>
        </div>
        <span class="room-stats">${room.clients === 0 ? minP : room.clients + (meta.botCount ?? 0)}\u2009/\u2009${room.maxClients} pilots</span>
        <button class="destroy-btn" data-id="${escHtml(room.roomId)}">DESTROY</button>`;
      row.querySelector<HTMLButtonElement>('.destroy-btn')!.addEventListener('click', () => {
        if (!confirm(`Destroy "${name}"?\n\nAll connected pilots will be disconnected.`)) return;
        void deleteRoom(room.roomId, name);
      });
      voidList.appendChild(row);
    }
  }

  async function deleteRoom(roomId: string, name: string): Promise<void> {
    try {
      const resp = await fetch(`/api/rooms/${encodeURIComponent(roomId)}`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
      if (!resp.ok) {
        const data = await resp.json() as { error?: string };
        throw new Error(data.error ?? `HTTP ${resp.status}`);
      }
      setStatus(`VOID "${name}" DESTROYED`);
      void fetchRooms();
    } catch (err) {
      setStatus(`DESTROY FAILED: ${String(err)}`, true);
    }
  }

  createBtn.addEventListener('click', async () => {
    const name             = newNameInput.value.trim();
    const roundDuration    = parseInt((document.getElementById('round-duration')    as HTMLSelectElement).value, 10);
    const obstacleDensity  = parseFloat((document.getElementById('obstacle-density') as HTMLSelectElement).value);
    const minimumPlayers   = parseInt((document.getElementById('min-players')        as HTMLSelectElement).value, 10);
    const difficulty       = (document.getElementById('difficulty') as HTMLSelectElement).value;
    if (!name) { setStatus('ENTER A VOID NAME', true); return; }
    createBtn.disabled = true;
    try {
      const resp = await fetch('/api/rooms', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...adminHeaders() },
        body:    JSON.stringify({ roomName: name, roundDuration, obstacleDensity, minimumPlayers, difficulty }),
      });
      if (!resp.ok) {
        const data = await resp.json() as { error?: string };
        throw new Error(data.error ?? `HTTP ${resp.status}`);
      }
      newNameInput.value = '';
      setStatus(`VOID "${name}" CREATED`);
      void fetchRooms();
    } catch (err) {
      setStatus(`CREATE FAILED: ${String(err)}`, true);
    } finally {
      createBtn.disabled = false;
    }
  });

  refreshBtn.addEventListener('click', () => { void fetchRooms(); });
  void fetchRooms();
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

void initAdminGate();
